import React, { useEffect, useState } from 'react'
import {
  Button,
  Divider,
  message,
  Modal,
  Typography,
  Upload,
  Space,
  notification
} from 'antd'

import { InboxOutlined } from '@ant-design/icons'
import QuestionList from './QuestionList'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'
import {
  submitAnswers,
  updateSubmission,
  deleteSubmissionFile
} from '../../../reducers/assessmentTakingReducer'
import {
  autoGrade,
  checkPlagiarism
} from '../../../reducers/submissionsReducer'
import FileDisplay from '../../../components/FileDisplay'
import SubmissionTimeTag from '../../../components/SubmissionTimeTag'

import { FlexSectionHeader } from '../../style'
import { DateTime } from 'luxon'

const { Title, Text } = Typography
const { Dragger } = Upload

const QuestionsSection = (props) => {
  const dispatch = useDispatch()
  const { courseId, assessmentId } = useParams()
  const navigate = useNavigate()
  const user = useSelector((state) => state.auth.user)
  const answers = useSelector(
    (state) => state.assessmentTaking.submission.answers
  )
  const [files, setFiles] = useState([])
  const { submission } = props
  const { assessment } = submission

  useEffect(() => {
    setFiles(
      submission.files.map((file) => {
        return { ...file, uid: file._id }
      })
    )
  }, [])

  const handleAnswerSubmit = (questionId, newAnswer) => {
    dispatch(
      submitAnswers(courseId, assessmentId, user._id, questionId, newAnswer)
    )
  }

  const handleFileRemove = async (removedFile) => {
    if (removedFile.url && !removedFile.originFileObj) {
      try {
        const hide = message.loading('Deleting file...', 0)

        dispatch(
          deleteSubmissionFile(
            courseId,
            assessmentId,
            user._id,
            removedFile.url
          )
        )

        hide()
        message.success('File deleted successfully')

        setFiles(files.filter((file) => file.uid !== removedFile.uid))
      } catch (error) {
        console.error('Error deleting file:', error)
        return false
      }
    } else {
      setFiles(files.filter((file) => file.uid !== removedFile.uid))
      message.info('File removed from upload queue')
    }
  }

  const handleFinish = (alreadySumbitted) => {
    if (
      assessment.questionsType === 'online' &&
      assessment.submissionType !== 'written'
    ) {
      if (answers.length !== assessment.questions.length) {
        notification.error({
          message: 'All questions must be answered to submit'
        })
        return
      }
    }

    if (assessment.submissionType === 'written' && files.length === 0) {
      message.error(`Written file must be uploaded to submit`)
      return
    }

    if (assessment.type === 'Exam') {
      Modal.confirm({
        title: 'Are you sure?',
        content: [
          <div key="1">Make sure all your answers are saved</div>,
          <div key="2">You won't be able to change them anymore</div>
        ],
        onOk() {
          dispatch(
            updateSubmission(courseId, assessmentId, user._id, {
              files: files,
              finished: true
            })
          ).then(() => {
            if (assessment.submissionType === 'written') {
              dispatch(checkPlagiarism(courseId, assessmentId))
            } else {
              dispatch(autoGrade(courseId, assessmentId))
            }
            window.close()
          })
        }
      })
    } else if (assessment.type === 'Assignment' && alreadySumbitted) {
      Modal.confirm({
        title: 'Are you sure?',
        content: [
          <div key="1">Your previous submission will be discarded.</div>
        ],

        onOk() {
          dispatch(
            updateSubmission(courseId, assessmentId, user._id, {
              files: files,
              finished: true
            })
          ).then(() => {
            if (assessment.submissionType === 'written') {
              dispatch(checkPlagiarism(courseId, assessmentId))
            } else {
              dispatch(autoGrade(courseId, assessmentId))
            }
            navigate(-1)
          })
        }
      })
    } else {
      dispatch(
        updateSubmission(courseId, assessmentId, user._id, {
          files: files,
          finished: true
        })
      ).then(() => {
        if (assessment.submissionType === 'written') {
          dispatch(checkPlagiarism(courseId, assessmentId))
        } else {
          dispatch(autoGrade(courseId, assessmentId))
        }
        navigate(-1)
      })
    }
  }

  return (
    <div
      style={{
        backgroundColor: '#fafafa',
        width: '95%',
        margin: '0 auto',
        borderRadius: '10px',
        padding: '20px 20px'
      }}
    >
      <FlexSectionHeader>
        <Space>
          <Title level={3}>{assessment?.title}</Title>
          {submission.submittedAt && assessment.type === 'Assignment' && (
            <>
              <Text type="secondary">Submitted at:</Text>
              {DateTime.fromISO(submission.submittedAt).toLocaleString(
                DateTime.DATETIME_MED
              )}
              <SubmissionTimeTag status={submission?.status} />
            </>
          )}
        </Space>
        {submission.finished && (
          <Button onClick={() => handleFinish(true)} type="default">
            <Text strong>Resubmit</Text>
          </Button>
        )}
        {!submission.finished && (
          <Button onClick={() => handleFinish()} type="primary">
            <Text style={{ color: 'white' }} strong>
              Submit
            </Text>
          </Button>
        )}
      </FlexSectionHeader>

      <Divider />

      {assessment?.questionsType === 'online' && (
        <QuestionList
          questions={assessment.questions}
          answers={answers}
          noOnlineAnswer={assessment.submissionType === 'written'}
          handleAnswerSubmit={handleAnswerSubmit}
        />
      )}

      {assessment?.questionsType === 'file' && (
        <FileDisplay files={assessment.files} />
      )}

      {assessment?.submissionType === 'written' && (
        <>
          <Divider />
          <Title level={5}>Upload your answer</Title>
          <Dragger
            onChange={(info) => {
              setFiles(info.fileList)
            }}
            onRemove={handleFileRemove}
            fileList={files}
            beforeUpload={() => false}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
          </Dragger>
        </>
      )}
    </div>
  )
}

export default QuestionsSection
